import { isIOS } from '@/helpers/userAgent'
import type { AudioDoc } from './logic'
import { storage as localStorageWrapper } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'

// The default rate is 1.1 as it sounds more natural.
const DEFAULT_RATE = 1.1
const MIN_RATE = 0.3
const MAX_RATE = 3.5

/**
 * Audio playbadck rate UI control (slower / falser).
 */
export class RateControl {
  private _audio: AudioDoc
  private _playbackRate: number = DEFAULT_RATE

  /**
   * The playback rate value when the rate control is initialised.
   *
   * This will be the stored rate in the localstorage. On iOS, when the
   * audio is loaded, we lose the saved playback rate because the
   * playback rate is reset to the default of `1.0`.
   *
   * - If this value is not `null`, when the audio is loaded, we restore
   * the playback rate to this value.
   * - This value is set to `null` when the playback rate is manually
   * changed (by the user).
   */
  private _loadedPlaybackRate: number | null = null

  constructor(audio: AudioDoc) {
    this._audio = audio

    this._playbackRate = this.getSavedRate()
    this._audio.playbackRate = this._playbackRate

    if (isIOS()) {
      // On iOS, the playback rate is reset to the default value of 1.0
      // when the audio is loaded. So, we store the playback rate before
      // loading the audio and restore it after loading it (when the canplay event is fired).
      this._loadedPlaybackRate = this._playbackRate

      this._audio.addListener('canplay', () => {
        if (this._loadedPlaybackRate) {
          this._audio.playbackRate = this._loadedPlaybackRate
        }
      })
    }

    this._audio.addListener('ratechange', () => {
      this._ratechangeHandler()
    })
  }

  /**
   * Get saved playback rate from local storage.
   * If no rate is saved, returns the `DEFAULT_RATE`.
   */
  getSavedRate(): number {
    const value = localStorageWrapper.getItem(StorageKeys.playerRate)
    if (value) {
      return parseFloat(value)
    }
    return DEFAULT_RATE
  }

  rate(): number {
    return this._playbackRate
  }

  reset(): void {
    this._playbackRate = DEFAULT_RATE
    this._audio.playbackRate = this._playbackRate
    this._loadedPlaybackRate = null
  }

  up(): void {
    this._playbackRate += 0.1
    if (this._playbackRate > MAX_RATE) {
      this._playbackRate = MAX_RATE
    }
    this._audio.playbackRate = this._playbackRate
    this._loadedPlaybackRate = null
  }

  down(): void {
    this._playbackRate -= 0.1
    if (this._playbackRate < MIN_RATE) {
      this._playbackRate = MIN_RATE
    }
    this._audio.playbackRate = this._playbackRate
    this._loadedPlaybackRate = null
  }

  _ratechangeHandler(): void {
    this._playbackRate = this._audio.playbackRate
    const value = Math.round(this._audio.playbackRate * 10) / 10
    localStorageWrapper.setItem(StorageKeys.playerRate, value.toString())
  }
}
